import { t } from "@travily/i18n";
import type ImageType from "@travily/types/shared/Image";
import { ActionPill } from "@ui/components/ActionPill";
import { Carousel } from "@ui/components/Carousel";
import { Dock } from "@ui/components/Dock";
import { FullBleed } from "@ui/components/FullBleed";
import { Icon } from "@ui/components/Icon";
import { Rating } from "@ui/components/Rating";
import { RichText } from "@ui/components/RichText";
import { SectionTab, SectionTabList, SectionTabPanel, SectionTabs } from "@ui/components/SectionTabs";
import { StandardButton } from "@ui/components/StandardButton";
import { useElementHeight } from "@ui/hooks/use-element-height";
import { useScrollSentinel } from "@ui/hooks/use-scroll-sentinel";
import type { ShareMethod } from "@ui/hooks/use-share";
import { tv } from "@ui/lib/tv";
import { Link } from "@ui/primitives/Link";
import { type ReactNode, useMemo } from "react";
import { Creator, type CreatorProps } from "./components/Creator";
import { Reviews, type ReviewsProps } from "./components/Reviews";

export interface ShowViewProps {
  averageScore: number | null;
  creator: CreatorProps;
  description: string | null;
  /**
   * When set, renders an enabled "Download" CTA in place of Buy, linking to the
   * buyer's file-download endpoint. Signals that the viewer has already
   * purchased the product. Opens in a new tab so the browser handles the file
   * response (the endpoint redirects to a signed download URL).
   */
  downloadHref?: string;
  /** One-sentence sales pitch shown below the rating and above the full description. */
  tagline?: string | null;
  favorited?: boolean;
  images: ImageType[];
  /**
   * When set, renders a "Manage product" button in place of Buy linking to
   * the seller's edit page. Takes precedence over downloadHref and
   * onPurchase.
   */
  manageHref?: string;
  onAddReview?: () => void;
  onFavoriteToggle?: () => void;
  onPurchase?: () => void;
  onShare?: () => void;
  priceLabel: string;
  priceNote?: string;
  reviewCount: number;
  reviews: ReviewsProps["reviews"];
  /** Wraps the rendered reviews list (e.g. in an infinite-scroll region) so
   *  pagination triggers sit inside the reviews panel, not at page bottom. */
  reviewsListWrapper?: (reviews: ReactNode) => ReactNode;
  shared?: ShareMethod;
  title: string;
}

type CtaState =
  | { kind: "manage"; href: string }
  | { kind: "download"; href: string }
  | { kind: "purchase"; onPurchase: () => void }
  | { kind: "none" };

const showView = tv({
  slots: {
    root: "",
    columns: "flex flex-col md:flex-row md:items-start md:gap-6 lg:gap-10",
    main: "md:min-w-0 md:flex-1",
    side: "md:min-w-0 md:flex-1 md:max-w-[508px]",
    tabsWrapper: "mt-6 md:mt-8",
    tabPanelContent: "mt-6 px-5 md:px-0",
    actionBar: "mb-5",
    title:
      "mt-4 text-[30px] font-extrabold leading-10 tracking-[-0.3px] text-neutral-900 md:mt-0 lg:text-4xl lg:leading-[44px] lg:tracking-[-0.36px]",
    tagline: "mt-4 text-lg font-bold leading-6 text-neutral-900 md:mt-5 md:text-xl md:leading-7",
    description: "mt-4 space-y-3 text-base leading-6 text-neutral-900 md:mt-5",
  },
});

export function ShowView({
  averageScore,
  creator,
  description,
  downloadHref,
  favorited,
  images,
  manageHref,
  onAddReview,
  onFavoriteToggle,
  onPurchase,
  onShare,
  priceLabel,
  priceNote,
  reviewCount,
  reviews,
  reviewsListWrapper,
  shared = false,
  tagline,
  title,
}: ShowViewProps) {
  const styles = showView();
  const { ref: dockMeasureRef, height: dockHeight } = useElementHeight<HTMLDivElement>();
  const rootMargin = useMemo(() => `0px 0px -${dockHeight}px 0px`, [dockHeight]);
  const [sentinelRef, isAtBottom] = useScrollSentinel({ rootMargin });
  const wrapReviews = reviewsListWrapper ?? ((reviewsNode: ReactNode) => reviewsNode);
  const ctaState: CtaState =
    manageHref !== undefined
      ? { kind: "manage", href: manageHref }
      : downloadHref !== undefined
        ? { kind: "download", href: downloadHref }
        : onPurchase !== undefined
          ? { kind: "purchase", onPurchase }
          : { kind: "none" };

  const renderCta = (size: "44" | "60", className: string) => {
    switch (ctaState.kind) {
      case "manage":
        return (
          <Link href={ctaState.href}>
            <StandardButton className={className} color="pink" size={size}>
              {t("components.views.products.show.manage_product")}
            </StandardButton>
          </Link>
        );
      case "download":
        return (
          <StandardButton
            className={className}
            color="pink"
            href={ctaState.href}
            rel="noopener noreferrer"
            size={size}
            target="_blank"
          >
            {t("components.views.products.show.download")}
          </StandardButton>
        );
      case "purchase":
        return (
          <StandardButton className={className} color="pink" size={size} onPress={ctaState.onPurchase}>
            {priceLabel}
          </StandardButton>
        );
      default:
        return null;
    }
  };

  const showsCta = ctaState.kind !== "none";
  const mobileCta = renderCta("44", "w-full");
  const desktopCta = renderCta("44", "mt-6 w-full lg:hidden");
  const desktopCtaLarge = renderCta("60", "mt-6 hidden w-full lg:flex");

  return (
    <div className={styles.root()}>
      <div className={styles.columns()}>
        {/* Main column */}
        <div className={styles.main()}>
          {/* Product image carousel */}
          <FullBleed>
            <Carousel aspect="square" mobile="scroll" images={images} priority sizes="(min-width: 768px) 50vw, 100vw" />
          </FullBleed>

          {/* Mobile: side content + tabs + footer */}
          <div className="md:hidden">
            {/* Title */}
            <h1 className={styles.title()}>{title}</h1>

            {/* Star rating */}
            {averageScore !== null && (
              <Rating className="mt-3" reviewCount={reviewCount} score={averageScore} size="20" variant="full" />
            )}

            {/* Mobile tabs — includes Summary */}
            <FullBleed className={styles.tabsWrapper()}>
              <SectionTabs defaultSelectedKey="summary">
                <SectionTabList>
                  <SectionTab id="summary">{t("components.views.products.show.tab_summary")}</SectionTab>
                  <SectionTab id="reviews">{t("components.views.products.show.tab_reviews")}</SectionTab>
                </SectionTabList>
                <SectionTabPanel id="summary">
                  <div className={styles.tabPanelContent()}>
                    {tagline && <p className={styles.tagline()}>{tagline}</p>}
                    {description && (
                      <div className={styles.description()}>
                        <RichText>{description}</RichText>
                      </div>
                    )}
                    <Creator {...creator} />
                  </div>
                </SectionTabPanel>
                <SectionTabPanel id="reviews">
                  <div className={styles.tabPanelContent()}>
                    {wrapReviews(<Reviews onAddReview={onAddReview} reviews={reviews} />)}
                  </div>
                </SectionTabPanel>
              </SectionTabs>
            </FullBleed>

            {/* Primary CTA — Manage / Download / Buy, docked to the viewport bottom */}
            {showsCta && (
              <>
                <div ref={sentinelRef} />
                <div ref={dockMeasureRef}>
                  <Dock hasOverflow={!isAtBottom}>{mobileCta}</Dock>
                </div>
              </>
            )}
          </div>

          {/* Desktop tabs — no Summary */}
          <FullBleed className={`hidden md:block ${styles.tabsWrapper()}`}>
            <SectionTabs defaultSelectedKey="reviews">
              <SectionTabList>
                <SectionTab id="reviews">{t("components.views.products.show.tab_reviews")}</SectionTab>
              </SectionTabList>
              <SectionTabPanel id="reviews">
                <div className={styles.tabPanelContent()}>
                  {wrapReviews(<Reviews onAddReview={onAddReview} reviews={reviews} />)}
                </div>
              </SectionTabPanel>
            </SectionTabs>
          </FullBleed>
        </div>

        {/* Side column — desktop only */}
        <div className={`hidden md:block ${styles.side()}`}>
          {/* Wishlist / Share action bar */}
          <div className={styles.actionBar()}>
            {/* Small action pill for md–lg */}
            <ActionPill className="lg:hidden" size="32">
              <ActionPill.ToggleButton
                aria-label={t("components.views.products.show.toggle_favorites")}
                hoverColor="pink"
                icon={<Icon name="heartInactive" shadow="none" type="sticker" />}
                isSelected={favorited}
                onChange={onFavoriteToggle}
                selectedIcon={<Icon name="heart" shadow="none" type="sticker" />}
                size="32"
              >
                {({ isSelected }) =>
                  isSelected
                    ? t("components.views.products.show.favorited")
                    : t("components.views.products.show.favorite")
                }
              </ActionPill.ToggleButton>

              <ActionPill.Button
                hoverColor="yellow"
                icon={<Icon name="share" shadow="none" type="sticker" />}
                onPress={onShare}
                size="32"
              >
                {shared === "clipboard"
                  ? t("components.views.products.show.copied")
                  : t("components.views.products.show.share")}
              </ActionPill.Button>
            </ActionPill>

            {/* Default action pill for lg+ */}
            <ActionPill className="hidden lg:flex">
              <ActionPill.ToggleButton
                aria-label={t("components.views.products.show.toggle_favorites")}
                hoverColor="pink"
                icon={<Icon name="heartInactive" shadow="none" type="sticker" />}
                isSelected={favorited}
                onChange={onFavoriteToggle}
                selectedIcon={<Icon name="heart" shadow="none" type="sticker" />}
              >
                {({ isSelected }) =>
                  isSelected
                    ? t("components.views.products.show.added_to_favorites")
                    : t("components.views.products.show.add_to_favorites")
                }
              </ActionPill.ToggleButton>

              <ActionPill.Button
                hoverColor="yellow"
                icon={<Icon name="share" shadow="none" type="sticker" />}
                onPress={onShare}
              >
                {shared === "clipboard"
                  ? t("components.views.products.show.link_copied")
                  : t("components.views.products.show.share_with_friends")}
              </ActionPill.Button>
            </ActionPill>
          </div>

          {/* Title */}
          <h1 className={styles.title()}>{title}</h1>

          {/* Star rating */}
          {averageScore !== null && (
            <>
              <Rating
                className="mt-3 lg:hidden"
                reviewCount={reviewCount}
                score={averageScore}
                size="20"
                variant="full"
              />
              <Rating className="mt-3 hidden lg:flex" reviewCount={reviewCount} score={averageScore} variant="full" />
            </>
          )}

          {/* Tagline */}
          {tagline && <p className={styles.tagline()}>{tagline}</p>}

          {/* Description — always visible on desktop */}
          {description && (
            <div className={styles.description()}>
              <RichText>{description}</RichText>
            </div>
          )}

          {/* Primary CTA — Manage / Download / Buy */}
          {showsCta && (
            <>
              {desktopCta}
              {desktopCtaLarge}
              {ctaState.kind === "purchase" && priceNote && (
                <p className="mt-2 text-center text-xs text-neutral-500">{priceNote}</p>
              )}
            </>
          )}

          <Creator {...creator} />
        </div>
      </div>
    </div>
  );
}
